import React, { lazy } from 'react';
import {
  MdFolderOpen,
  MdOutlineUploadFile,
  MdReplay,
  MdIntegrationInstructions,
  MdLightbulb, MdRocketLaunch, MdChatBubble,
  MdWeb,
  MdHistory,
  MdTrendingUp,
  MdNotifications,
  MdDescription,
  MdCompareArrows,
  MdAdminPanelSettings,
  MdAutoFixHigh,
  MdAssessment,
  MdPaid,
  MdFlag,
  MdRoute,
  MdSearch,
  MdHelpOutline,
  MdLink,
  MdSpaceDashboard,
  MdMoreHoriz,
  MdAutoAwesome,
  MdTravelExplore,
} from 'react-icons/md';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { PiFolderFill } from 'react-icons/pi';
import { CgInsights } from 'react-icons/cg';
import { GiCrossedSwords } from 'react-icons/gi';
import { FaLinkedin, FaReddit } from 'react-icons/fa';
import CustomTopicsPage from '../../../../../pages/private/feedback/CustomTopicsPage';
import ApiKeysPage from '../../../../../pages/private/account/ApiKeysPage';
import IntegrationsPage from '../../../../../pages/private/account/IntegrationsPage';
import { TbWand } from 'react-icons/tb';

// Lazy-loaded pages
const NpsAnalysisPage = lazy(() => import('../../../../../pages/private/reporting/NpsAnalysisPage'));
const UploadSurveyDataPage = lazy(() => import('../../../../../pages/private/reporting/UploadSurveyDataPage'));
const SessionPlaylistPage = lazy(() => import('../../../../../pages/private/screen_record/SessionPlaylistPage'));
const SessionRecordScriptPage = lazy(() => import('../../../../../pages/private/screen_record/SessionRecordScriptPage'));
const KanbanPage = lazy(() => import('../../../../../pages/private/roadmap/KanbanPage'));
const GetStartedPage = lazy(() => import('../../../../../pages/private/home/GetStartedPage'));
const FeedbackAnalysisPage = lazy(() => import('../../../../../pages/private/feedback/FeedbackAnalysisPage'));
const WebsiteAnalysisPage = lazy(() => import('../../../../../pages/private/website-analysis/WebsiteAnalysisPage'));
const UnifiedWebsiteAnalysisPage = lazy(() => import('../../../../../pages/public/private-reports/UnifiedWebsiteAnalysisPage'));
const ReportsPage = lazy(() => import('../../../../../pages/private/website-analysis/ReportsPage'));
const ReportsList = lazy(() => import('./ReportsList'));

// Intelligence pages
const CompetitorIntelligencePage = lazy(() => import('../../../../../pages/private/intelligence/CompetitorIntelligencePage'));
const CompetitorDetailPage = lazy(() => import('../../../../../pages/private/intelligence/CompetitorDetailPage'));
const IntelligenceFeedPage = lazy(() => import('../../../../../pages/private/intelligence/IntelligenceFeedPage'));
const LinkedInPostsPage = lazy(() => import('../../../../../pages/private/intelligence/LinkedInPostsPage'));
const ComparisonsPage = lazy(() => import('../../../../../pages/private/intelligence/ComparisonsPage'));
const ActionItemsHub = lazy(() => import('../../../../../pages/private/intelligence/ActionItemsHub'));
const SocialPage = lazy(() => import('../../../../../pages/private/intelligence/SocialPage'));

// Admin pages (sys_admin only)
const CreditAdminPage = lazy(() => import('../../../../../pages/private/admin/CreditAdminPage'));
const AdminFlowsPage = lazy(() => import('../../../../../pages/private/admin/AdminFlowsPage'));
const AdminCostsPage = lazy(() => import('../../../../../pages/private/admin/AdminCostsPage'));
const AdminCoveragePage = lazy(() => import('../../../../../pages/private/admin/AdminCoveragePage'));
const AdminReferralsPage = lazy(() => import('../../../../../pages/private/admin/AdminReferralsPage'));
const AdminFunnelScansPage = lazy(() => import('../../../../../pages/private/admin/AdminFunnelScansPage'));

// SEO pages — analysis-first IA (Round B–D).
//   Keywords / LLM Questions / Backlinks   = the analysis surfaces
//                                              users actually live in
//   Tools                                  = launcher (results land
//                                              on the analysis pages)
//   History                                = raw run audit trail
//
// Action Plan was the playbook view; dropped from the sidebar in
// Round D (the per-page "Next actions" widgets replaced it). The
// page file is still in the repo for now in case we want to bring
// the playbook orchestrator back as a power-user feature.
const SeoKeywordsPage   = lazy(() => import('../../../../../pages/private/seo/SeoKeywordsPage'));
const SeoQuestionsPage  = lazy(() => import('../../../../../pages/private/seo/SeoQuestionsPage'));
const SeoBacklinksPage  = lazy(() => import('../../../../../pages/private/seo/SeoBacklinksPage'));
const SeoToolsPage      = lazy(() => import('../../../../../pages/private/seo/SeoToolsPage'));
const SeoHistoryPage    = lazy(() => import('../../../../../pages/private/seo/SeoHistoryPage'));

const iconStyle = {};

interface Dictionary<T> {
  [Key: string]: T;
}

export interface Category {
  name: string;
  id?: string;
  path?: string;
  icon?: JSX.Element;
  title?: boolean;
  folder?: boolean;
  child?: boolean;
  sticky?: boolean;
  badge?: boolean;
  badgeNumber?: Number;
  children?: Category[];
  divider?: boolean;
  component?: Function;
  params?: Dictionary<string>;
  canHaveSubcategories?: boolean;
  defaultOpen?: boolean;
}

export const categories: Category[] = [
  {
    // Single primary group. The three items below stay visible at all times;
    // everything else lives under the collapsed "More" submenu.
    name: '',
    id: 'primary',
    children: [
      // The primary menu IS the user journey, in order:
      // 1. Where do I stand? → 2. What do I do next? → 3. Who am I against?
      // Everything analytical/secondary lives under More so first-time users
      // never face a wall of features.
      {
        name: 'AI Visibility',
        path: 'intelligence/ai-visibility',
        icon: <MdAutoAwesome style={iconStyle} />,
      },
      {
        name: 'Action items',
        path: 'intelligence/action-items',
        component: ActionItemsHub,
        icon: <MdRocketLaunch style={iconStyle} />,
      },
      {
        name: 'Competitors',
        path: 'intelligence/competitors',
        component: CompetitorIntelligencePage,
        icon: <GiCrossedSwords style={iconStyle} />,
      },
      {
        name: 'More',
        id: 'more',
        defaultOpen: false,
        icon: <MdMoreHoriz style={iconStyle} />,
        children: [
          {
            name: 'Scorecard',
            // The full aspect grid (Competitive Position) — deep analysis.
            path: 'intelligence/grid',
            icon: <MdSpaceDashboard style={iconStyle} />,
          },
          {
            name: 'Progress',
            path: 'intelligence/progress',
            icon: <MdTrendingUp style={iconStyle} />,
          },
          {
            name: 'Reports',
            path: 'market_trend_reporting/reports',
            component: ReportsPage,
            icon: <MdHistory style={iconStyle} />,
          },
          {
            name: 'Integrations',
            path: 'settings/integrations',
            component: IntegrationsPage,
            icon: <MdLink style={iconStyle} />,
          },
          {
            name: 'Activity Feed',
            path: 'intelligence/feed',
            component: IntelligenceFeedPage,
            icon: <MdNotifications style={iconStyle} />,
          },
          {
            name: 'Comparisons',
            path: 'intelligence/comparisons',
            component: ComparisonsPage,
            icon: <MdCompareArrows style={iconStyle} />,
          },
          {
            name: 'SEO',
            id: 'seo',
            defaultOpen: false,
            icon: <MdSearch style={iconStyle} />,
            children: [
              { name: 'Keywords', path: 'seo/keywords', component: SeoKeywordsPage, icon: <MdSearch style={iconStyle} /> },
              { name: 'LLM Questions', path: 'seo/questions', component: SeoQuestionsPage, icon: <MdHelpOutline style={iconStyle} /> },
              { name: 'Backlinks', path: 'seo/backlinks', component: SeoBacklinksPage, icon: <MdLink style={iconStyle} /> },
              { name: 'Tools', path: 'seo/tools', component: SeoToolsPage, icon: <MdAutoFixHigh style={iconStyle} /> },
              { name: 'History', path: 'seo/history', component: SeoHistoryPage, icon: <MdAssessment style={iconStyle} /> },
            ],
          },
          {
            name: 'API Keys',
            path: 'settings/api-keys',
            component: ApiKeysPage,
            icon: <MdIntegrationInstructions style={iconStyle} />,
          },
        ],
      },
    ],
  },
  // Temporarily hidden sections - uncomment when ready to work on them
  /*
  {
    name: 'Getting Started',
    title: true,
  },
  {
    name: '',
    path: 'get_started',
    id: 'get_started',
    children: [
      {
        name: 'Getting Started',
        path: 'get_started',
        component: GetStartedPage,
        icon: <MdRocketLaunch style={iconStyle} />,
      },
    ],
  },
  { name: 'Feedback', title: true },
  {
    name: '',
    path: 'feedback',
    id: 'feedback',
    children: [
      {
        name: 'Ask AI',
        path: 'ask_ai',
        component: FeedbackAnalysisPage,
        icon: <TbWand style={iconStyle} />,
      },
            {
        name: 'Custom topics',
        path: 'custom_topics',
        component: CustomTopicsPage,
        icon: <MdLightbulb style={iconStyle} />,
      },
      {
        name: 'Analysis',
        path: 'analysis',
        component: FeedbackAnalysisPage,
        icon: <CgInsights style={iconStyle} />,
      },


    ],
  },
  { name: 'Survey', title: true },
  {
    name: '',
    path: 'survey',
    id: 'survey',
    children: [
      {
        name: 'Upload Survey Data',
        path: 'upload_survey_data',
        component: UploadSurveyDataPage,
        icon: <MdOutlineUploadFile style={iconStyle} />,
      },
      {
        name: 'Reports',
        id: 'reporting',
                      path: 'nps_analysis',
        icon: <HiOutlineDocumentReport style={iconStyle} />,
        children: [
          {
            name: 'AI Generated',
            id: 'ai-generated',
            path: 'AI',
            component: NpsAnalysisPage,
            icon: <PiFolderFill style={iconStyle} />,
          },
          // Other sub-categories...
        ],
      },
    ],
  },


  { name: 'Roadmap Planning', title: true },
  {
    name: '',
    path: 'roadmap',
    id: 'roadmap',
    children: [
      {
        name: 'Roadmaps',
        id: 'roadmaps',
        path: 'roadmaps',
        component: KanbanPage,
        icon: <PiFolderFill style={iconStyle} />,
        canHaveSubcategories: true,
        children: [
          // Additional sub-categories can be added here
        ],
      },
    ],
  },
  */
];

/**
 * Admin-only navigation. Surfaces in the sidebar AND in the route table
 * only when localStorage.is_sys_admin === 'true' (set by AuthContext at
 * login / token validate). Backend endpoints are also gated by
 * is_sys_admin so a stripped UI cannot call admin APIs.
 */
export const adminCategories: Category[] = [
  { name: 'Admin', title: true, id: 'admin_section' },
  {
    name: '',
    path: 'admin',
    id: 'admin',
    children: [
      {
        name: 'Credits',
        path: 'credits',
        component: CreditAdminPage,
        icon: <MdAdminPanelSettings style={iconStyle} />,
      },
      {
        name: 'AI Flows',
        path: 'flows',
        component: AdminFlowsPage,
        icon: <MdAssessment style={iconStyle} />,
      },
      {
        name: 'Cost',
        path: 'costs',
        component: AdminCostsPage,
        icon: <MdPaid style={iconStyle} />,
      },
      {
        name: 'Coverage gaps',
        path: 'coverage',
        component: AdminCoveragePage,
        icon: <MdFlag style={iconStyle} />,
      },
      {
        name: 'Referrals',
        path: 'referrals',
        component: AdminReferralsPage,
        icon: <MdLink style={iconStyle} />,
      },
      {
        name: 'Funnel Leads',
        path: 'funnel-leads',
        component: AdminFunnelScansPage,
        icon: <MdTravelExplore style={iconStyle} />,
      },
    ],
  },
];